import { Controller } from "@hotwired/stimulus"

// Drives the inline "split a timecard" panel on the detail screen. The Split
// action button reveals the panel (date picker + day steppers + intro) and turns
// the merged hours/expenses table into a live Timecard 1 / Timecard 2 split: as
// the user picks the split date, line-item rows move between the two sections, the
// section headers update, and the confirm button is enabled only when the date
// sits inside the card's period (period_start <= split < period_end — mirrors
// Cup::TimeCards::SplitForm). The split is day-granular, so only the date matters.
const DATE_PLACEHOLDER = "__DATE__"

export default class extends Controller {
  static targets = ["panel", "input", "submit", "row",
                    "tc1Body", "tc2Body", "tc1Divider", "tc2Divider",
                    "tc1Header", "tc2Header", "tc1Empty", "tc2Empty", "totals"]
  static values = {
    periodStart: String,
    periodEnd: String,
    defaultDate: String,
    dayNames: { type: Array, default: [] },
    tc1Label: String,
    tc2Label: String,
    tc1UntilTemplate: String,
    tc2FromTemplate: String,
    splitting: { type: Boolean, default: false },
  }

  connect() {
    this.orderedRows = [...this.rowTargets]
    this.render()
  }

  // The Split action button toggles the split panel open/closed.
  toggle() {
    this.splittingValue = !this.splittingValue
    if (this.splittingValue && !this.inputTarget.value) {
      this.inputTarget.value = this.defaultDateValue || this.periodStartValue || ""
    }
    this.render()
  }

  // input->timecard-split#update on the date picker.
  update() {
    this.render()
  }

  // The −1 / +1 day steppers (data-step="-1" / "1"), clamped to the valid window.
  stepDay(event) {
    const step = Number(event.params.step || event.currentTarget.dataset.step || 0)
    const current = this.splitDate() || this.defaultDateValue || this.periodStartValue
    if (!current) return

    let next = this.addDays(current, step)
    const min = this.periodStartValue
    const max = this.maxSplitDate()
    if (min && next < min) next = min
    if (max && next > max) next = max
    this.inputTarget.value = next
    this.render()
  }

  render() {
    const active = this.splittingValue
    if (this.hasPanelTarget) this.panelTarget.hidden = !active
    // TC1's section title row shows only while splitting; TC2's whole tbody
    // (its title + repeated headers + rows) hides as one unit when not.
    this.tc1DividerTargets.forEach(el => (el.hidden = !active))
    if (this.hasTc2BodyTarget) this.tc2BodyTarget.hidden = !active
    // The grand total is meaningless mid-split (rows span both cards) — hide it.
    if (this.hasTotalsTarget) this.totalsTarget.hidden = active

    const splitDate = active ? this.splitDate() : null
    this.distribute(splitDate)
    this.refreshHeaders(splitDate)
    this.refreshSubmit(active, splitDate)
  }

  distribute(splitDate) {
    let tc1 = 0
    let tc2 = 0
    this.orderedRows.forEach(row => {
      const onTc1 = !splitDate || this.belongsToFirstCard(row, splitDate)
      const body = onTc1 ? this.tc1BodyTarget : this.tc2BodyTarget
      const anchor = onTc1 ? this.tc1EmptyTarget : this.tc2EmptyTarget
      body.insertBefore(row, anchor)
      onTc1 ? tc1++ : tc2++
    })
    this.tc1EmptyTarget.hidden = tc1 > 0
    if (this.hasTc2EmptyTarget) this.tc2EmptyTarget.hidden = tc2 > 0
  }

  // A row with no worked_on date (period-level total/allowance) stays on the
  // first card, matching the service's NULL handling.
  belongsToFirstCard(row, splitDate) {
    const workedOn = row.dataset.workedOn
    if (!workedOn) return true
    return workedOn <= splitDate
  }

  refreshHeaders(splitDate) {
    if (splitDate) {
      const start = this.formatDisplay(splitDate)
      const next = this.formatDisplay(this.addDays(splitDate, 1))
      this.tc1HeaderTargets.forEach(el => (el.textContent = this.tc1UntilTemplateValue.replace(DATE_PLACEHOLDER, start)))
      this.tc2HeaderTargets.forEach(el => (el.textContent = this.tc2FromTemplateValue.replace(DATE_PLACEHOLDER, next)))
    } else {
      this.tc1HeaderTargets.forEach(el => (el.textContent = this.tc1LabelValue))
      this.tc2HeaderTargets.forEach(el => (el.textContent = this.tc2LabelValue))
    }
  }

  refreshSubmit(active, splitDate) {
    if (!this.hasSubmitTarget) return

    const valid = active && Boolean(splitDate) &&
      Boolean(this.periodStartValue) && Boolean(this.periodEndValue) &&
      splitDate >= this.periodStartValue && splitDate < this.periodEndValue
    this.submitTarget.disabled = !valid
  }

  maxSplitDate() {
    return this.periodEndValue ? this.addDays(this.periodEndValue, -1) : null
  }

  splitDate() {
    const value = this.inputTarget.value
    return value ? value.slice(0, 10) : null
  }

  // Weekday (localized, Sunday-indexed day_names) + European date, e.g.
  // "Wednesday 18-01-2026".
  formatDisplay(iso) {
    const [y, m, d] = iso.split("-").map(Number)
    const weekday = this.dayNamesValue[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] || ""
    const dd = String(d).padStart(2, "0")
    const mm = String(m).padStart(2, "0")
    return `${weekday} ${dd}-${mm}-${y}`.trim()
  }

  addDays(iso, amount) {
    const [y, m, d] = iso.split("-").map(Number)
    const date = new Date(Date.UTC(y, m - 1, d))
    date.setUTCDate(date.getUTCDate() + amount)
    const yy = date.getUTCFullYear()
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0")
    const dd = String(date.getUTCDate()).padStart(2, "0")
    return `${yy}-${mm}-${dd}`
  }
}
